import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const uid = (p) => `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const defaultData = (type) => (type === "guess" ? { answers: [""] } : type === "matching"
  ? { pairs: [1, 2, 3].map(() => ({ id: uid("p"), left: "", right: "" })) }
  : { categories: [{ id: "c1", name: "" }, { id: "c2", name: "" }], items: [{ id: uid("i"), text: "", category_id: "c1" }, { id: uid("i"), text: "", category_id: "c2" }] });

const RemoveBtn = ({ onClick, testId }) => (
  <Button type="button" variant="ghost" size="icon" onClick={onClick} aria-label="Sil" data-testid={testId}><Trash2 className="h-4 w-4" /></Button>
);

const AddBtn = ({ onClick, testId, children }) => (
  <Button type="button" variant="outline" onClick={onClick} data-testid={testId}><Plus className="h-4 w-4 mr-1" />{children}</Button>
);

export const GuessFields = ({ form, update, track }) => {
  const answers = form.data.answers || [];
  const set = (list) => update({ data: { answers: list } });
  return (
    <div className="space-y-2" data-testid="guess-fields">
      <p className="text-sm text-slate-400">Kabul edilen cevaplar (1-10). Büyük/küçük harf, Türkçe karakter ve alt simge farkı yok sayılır. Cevaplar tahtada gizli kalır.</p>
      {answers.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={a} onFocus={(e) => track(`a:${i}`, e.target)} onChange={(e) => set(answers.map((x, j) => (j === i ? e.target.value : x)))}
            placeholder={i === 0 ? "Ana cevap" : "Alternatif yazım"} className="h-11" data-testid={`guess-answer-${i}`} />
          {answers.length > 1 && <RemoveBtn onClick={() => set(answers.filter((_, j) => j !== i))} testId={`guess-answer-remove-${i}`} />}
        </div>
      ))}
      {answers.length < 10 && <AddBtn onClick={() => set([...answers, ""])} testId="guess-answer-add">Cevap ekle</AddBtn>}
    </div>
  );
};

export const MatchingFields = ({ form, update, track }) => {
  const pairs = form.data.pairs || [];
  const set = (list) => update({ data: { pairs: list } });
  const edit = (i, patch) => set(pairs.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <div className="space-y-2" data-testid="matching-fields">
      <p className="text-sm text-slate-400">Her satıra doğru eşleşen bir çift yazın. Öğrenci sağ sütunu karışık sırada görür (2-6 çift).</p>
      {pairs.map((p, i) => (
        <div key={p.id} className="flex items-center gap-2" data-testid={`pair-row-${i}`}>
          <Input value={p.left} onFocus={(e) => track(`d:pairs:${i}:left`, e.target)} onChange={(e) => edit(i, { left: e.target.value })}
            placeholder="Sol ifade" className="h-11" data-testid={`pair-left-${i}`} />
          <span className="text-[#FACC15] font-bold">↔</span>
          <Input value={p.right} onFocus={(e) => track(`d:pairs:${i}:right`, e.target)} onChange={(e) => edit(i, { right: e.target.value })}
            placeholder="Eşi" className="h-11" data-testid={`pair-right-${i}`} />
          {pairs.length > 2 && <RemoveBtn onClick={() => set(pairs.filter((_, j) => j !== i))} testId={`pair-remove-${i}`} />}
        </div>
      ))}
      {pairs.length < 6 && <AddBtn onClick={() => set([...pairs, { id: uid("p"), left: "", right: "" }])} testId="pair-add-button">Çift ekle</AddBtn>}
    </div>
  );
};

const ItemRow = ({ it, i, cats, edit, track, canRemove, onRemove }) => (
  <div className="rounded-lg border border-[rgba(71,85,105,0.45)] p-2 space-y-2" data-testid={`item-row-${i}`}>
    <div className="flex items-center gap-2">
      <Input value={it.text} onFocus={(e) => track(`d:items:${i}:text`, e.target)} onChange={(e) => edit(i, { text: e.target.value })}
        placeholder="Öğe" className="h-10" data-testid={`item-text-${i}`} />
      {canRemove && <RemoveBtn onClick={onRemove} testId={`item-remove-${i}`} />}
    </div>
    <div className="flex flex-wrap gap-1.5">
      {cats.map((c, k) => (
        <button key={c.id} type="button" onClick={() => edit(i, { category_id: c.id })} data-testid={`item-${i}-cat-${k}`}
          className={`px-3 py-1 rounded-md border text-sm font-semibold ${it.category_id === c.id ? "bg-[#34D399] text-[#05070D] border-[#34D399]" : "border-[rgba(71,85,105,0.45)] text-slate-300"}`}>
          {c.name || `Grup ${k + 1}`}
        </button>
      ))}
    </div>
  </div>
);

export const ClassificationFields = ({ form, update, track }) => {
  const { categories: cats = [], items = [] } = form.data;
  const set = (patch) => update({ data: { categories: cats, items, ...patch } });
  const editCat = (i, name) => set({ categories: cats.map((c, j) => (j === i ? { ...c, name } : c)) });
  const removeCat = (i) => {
    const rest = cats.filter((_, j) => j !== i);
    set({ categories: rest, items: items.map((it) => (rest.some((c) => c.id === it.category_id) ? it : { ...it, category_id: rest[0].id })) });
  };
  const editItem = (i, patch) => set({ items: items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });
  return (
    <div className="space-y-4" data-testid="classification-fields">
      <div className="space-y-2">
        <p className="text-sm text-slate-400">Gruplar (2-4)</p>
        {cats.map((c, i) => (
          <div key={c.id} className="flex items-center gap-2">
            <Input value={c.name} onFocus={(e) => track(`d:categories:${i}:name`, e.target)} onChange={(e) => editCat(i, e.target.value)}
              placeholder={`Grup ${i + 1} adı`} className="h-11" data-testid={`category-name-${i}`} />
            {cats.length > 2 && <RemoveBtn onClick={() => removeCat(i)} testId={`category-remove-${i}`} />}
          </div>
        ))}
        {cats.length < 4 && <AddBtn onClick={() => set({ categories: [...cats, { id: uid("c"), name: "" }] })} testId="category-add-button">Grup ekle</AddBtn>}
      </div>
      <div className="space-y-2">
        <p className="text-sm text-slate-400">Öğeler (2-12) · her öğenin doğru grubunu işaretleyin</p>
        {items.map((it, i) => (
          <ItemRow key={it.id} it={it} i={i} cats={cats} edit={editItem} track={track} canRemove={items.length > 2}
            onRemove={() => set({ items: items.filter((_, j) => j !== i) })} />
        ))}
        {items.length < 12 && <AddBtn onClick={() => set({ items: [...items, { id: uid("i"), text: "", category_id: cats[0]?.id }] })} testId="item-add-button">Öğe ekle</AddBtn>}
      </div>
    </div>
  );
};
